<script lang="ts">
	/**
	 * ExhibitScheduling — schedule slots with no-show prediction +
	 * receptionist call queue.
	 *
	 * Beat 04: Scheduling + the AI receptionist.
	 * Pain anchor: 23% national no-show rate (MGMA 2024); 42% of incoming
	 *              calls go unanswered at small practices (Insight Health 2025).
	 *
	 * Visual: split into two halves —
	 *   Top:    today's schedule with no-show risk on each slot, one
	 *           rebook suggested from the waitlist.
	 *   Bottom: live receptionist queue showing what was handled, what
	 *           was routed to the front desk.
	 *
	 * Integrated partner: voice + chat receptionist (Hippocratic / Hyro /
	 * Klara). Ekavi orchestrates routing; the partner handles the call.
	 */
</script>

<figure
	class="exhibit"
	aria-label="Today's schedule with no-show risk per slot and a sample receptionist call queue"
>
	<div class="head">
		<span class="eyebrow">Today · 9:00am – 12:00pm · Dr. Rivera</span>
		<span class="risk-key">no-show risk</span>
	</div>

	<ul class="slots">
		<li class="slot">
			<span class="time">9:00</span>
			<span class="patient">Adeyemi, T.</span>
			<span class="risk risk-low">low</span>
			<span class="state">confirmed</span>
		</li>
		<li class="slot">
			<span class="time">9:30</span>
			<span class="patient">Patel, S.</span>
			<span class="risk risk-high">high · 78%</span>
			<span class="state state-warn">double-booked</span>
		</li>
		<li class="slot">
			<span class="time">10:00</span>
			<span class="patient">Hernandez, M.</span>
			<span class="risk risk-low">low</span>
			<span class="state">confirmed</span>
		</li>
		<li class="slot">
			<span class="time">10:30</span>
			<span class="patient">Wong, J.</span>
			<span class="risk risk-low">low</span>
			<span class="state">confirmed</span>
		</li>
		<li class="slot slot-filled">
			<span class="time">11:00</span>
			<span class="patient">Kovacs, L. · waitlist</span>
			<span class="risk"></span>
			<span class="state state-good">filled by Ekavi</span>
		</li>
	</ul>

	<div class="reception">
		<p class="reception-eyebrow">
			<span class="reception-icon" aria-hidden="true">
				<svg viewBox="0 0 16 16" width="13" height="13" fill="currentColor" focusable="false">
					<path
						d="M3.654 1.328a.678.678 0 00-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 004.168 6.608 17.569 17.569 0 006.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 00-.063-1.015l-2.307-1.794a.678.678 0 00-.58-.122l-2.19.547a1.745 1.745 0 01-1.657-.459L5.482 8.643a1.745 1.745 0 01-.46-1.657l.548-2.19a.678.678 0 00-.122-.58L3.654 1.328z"
					/>
				</svg>
				<span class="pulse-dot" aria-hidden="true"></span>
			</span>
			<span class="reception-label">Voice + chat receptionist · last hour</span>
		</p>
		<ul class="calls">
			<li class="call">
				<span class="call-channel call-channel-voice" aria-label="voice call">
					<svg viewBox="0 0 16 16" width="11" height="11" fill="currentColor" focusable="false">
						<path
							d="M3.654 1.328a.678.678 0 00-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 004.168 6.608 17.569 17.569 0 006.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 00-.063-1.015l-2.307-1.794a.678.678 0 00-.58-.122l-2.19.547a1.745 1.745 0 01-1.657-.459L5.482 8.643a1.745 1.745 0 01-.46-1.657l.548-2.19a.678.678 0 00-.122-.58L3.654 1.328z"
						/>
					</svg>
				</span>
				<span class="call-time">8:42</span>
				<span class="call-desc">Refill request, methotrexate</span>
				<span class="call-route">routed → MA</span>
			</li>
			<li class="call">
				<span class="call-channel call-channel-voice" aria-label="voice call">
					<svg viewBox="0 0 16 16" width="11" height="11" fill="currentColor" focusable="false">
						<path
							d="M3.654 1.328a.678.678 0 00-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 004.168 6.608 17.569 17.569 0 006.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 00-.063-1.015l-2.307-1.794a.678.678 0 00-.58-.122l-2.19.547a1.745 1.745 0 01-1.657-.459L5.482 8.643a1.745 1.745 0 01-.46-1.657l.548-2.19a.678.678 0 00-.122-.58L3.654 1.328z"
						/>
					</svg>
				</span>
				<span class="call-time">8:51</span>
				<span class="call-desc">Reschedule request, cardiology f/u</span>
				<span class="call-route call-route-self">handled · rebooked</span>
			</li>
			<li class="call">
				<span class="call-channel call-channel-chat" aria-label="chat message">
					<svg viewBox="0 0 16 16" width="11" height="11" fill="currentColor" focusable="false">
						<path
							d="M2.5 2A1.5 1.5 0 001 3.5v8A1.5 1.5 0 002.5 13h2v2.25c0 .224.272.336.43.177L7.78 13h5.72A1.5 1.5 0 0015 11.5v-8A1.5 1.5 0 0013.5 2h-11z"
						/>
					</svg>
				</span>
				<span class="call-time">9:03</span>
				<span class="call-desc">Insurance question, BCBS plan change</span>
				<span class="call-route">routed → front desk</span>
			</li>
		</ul>
	</div>
</figure>

<style>
	.exhibit {
		background: var(--ekavi-paper);
		border: var(--ekavi-stroke-hairline);
		padding: var(--ekavi-space-6);
		margin: 0;
	}
	.head {
		display: flex;
		justify-content: space-between;
		align-items: baseline;
		gap: var(--ekavi-space-3);
		padding-bottom: var(--ekavi-space-3);
		border-bottom: var(--ekavi-stroke-hairline);
		margin-bottom: var(--ekavi-space-4);
	}
	.eyebrow {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		letter-spacing: var(--ekavi-track-caps);
		text-transform: uppercase;
		color: var(--ekavi-ink-3);
	}
	.risk-key {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-3);
		text-transform: lowercase;
	}

	.slots {
		list-style: none;
		padding: 0;
		margin: 0 0 var(--ekavi-space-6);
	}
	.slot {
		display: grid;
		grid-template-columns: 56px minmax(0, 1fr) 96px auto;
		align-items: center;
		gap: var(--ekavi-space-3);
		padding-block: var(--ekavi-space-2);
		border-bottom: var(--ekavi-stroke-hairline);
	}
	.slot:last-child {
		border-bottom: none;
	}
	.slot-filled {
		border-bottom-color: var(--ekavi-sage);
	}
	.time {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-14);
		color: var(--ekavi-ink-2);
		font-variant-numeric: tabular-nums;
	}
	.patient {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-14);
		color: var(--ekavi-ink-1);
		font-weight: var(--ekavi-weight-halbfett);
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.risk {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-3);
	}
	.risk-high {
		color: var(--ekavi-ochre);
		font-weight: var(--ekavi-weight-halbfett);
	}
	.risk-low {
		color: var(--ekavi-ink-3);
	}
	.state {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-2);
		text-transform: lowercase;
	}
	.state-warn {
		color: var(--ekavi-ochre);
	}
	.state-good {
		color: var(--ekavi-sage);
		font-weight: var(--ekavi-weight-halbfett);
	}

	.reception {
		padding-top: var(--ekavi-space-4);
		border-top: var(--ekavi-stroke-hairline);
	}
	.reception-eyebrow {
		display: flex;
		align-items: center;
		gap: var(--ekavi-space-2);
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		letter-spacing: var(--ekavi-track-caps);
		text-transform: uppercase;
		color: var(--ekavi-ink-3);
		margin: 0 0 var(--ekavi-space-3);
		font-weight: var(--ekavi-weight-halbfett);
	}
	.reception-icon {
		position: relative;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		color: var(--ekavi-charcoal);
		line-height: 0;
	}
	.pulse-dot {
		position: absolute;
		top: -2px;
		right: -3px;
		width: 6px;
		height: 6px;
		border-radius: 50%;
		background: var(--ekavi-sage, #5a8a6a);
		box-shadow: 0 0 0 0 currentColor;
		animation: pulse 2s ease-out infinite;
		color: var(--ekavi-sage, #5a8a6a);
	}
	@keyframes pulse {
		0% {
			box-shadow: 0 0 0 0 color-mix(in srgb, currentColor 60%, transparent);
		}
		70% {
			box-shadow: 0 0 0 6px color-mix(in srgb, currentColor 0%, transparent);
		}
		100% {
			box-shadow: 0 0 0 0 color-mix(in srgb, currentColor 0%, transparent);
		}
	}
	@media (prefers-reduced-motion: reduce) {
		.pulse-dot {
			animation: none;
		}
	}
	.reception-label {
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.calls {
		list-style: none;
		padding: 0;
		margin: 0;
	}
	.call {
		display: grid;
		grid-template-columns: 18px 44px minmax(0, 1fr) auto;
		align-items: center;
		gap: var(--ekavi-space-3);
		padding-block: var(--ekavi-space-2);
		border-bottom: var(--ekavi-stroke-hairline);
	}
	.call:last-child {
		border-bottom: none;
	}
	.call-channel {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		line-height: 0;
	}
	.call-channel-voice {
		color: var(--ekavi-charcoal);
	}
	.call-channel-chat {
		color: var(--ekavi-ink-3);
	}
	.call-time {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-3);
		font-variant-numeric: tabular-nums;
	}
	.call-desc {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-14);
		color: var(--ekavi-ink-1);
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.call-route {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-2);
	}
	.call-route-self {
		color: var(--ekavi-sage);
		font-weight: var(--ekavi-weight-halbfett);
	}

	@media (max-width: 480px) {
		.slot {
			grid-template-columns: 56px 1fr auto;
		}
		.risk {
			grid-column: 2 / 3;
			grid-row: 2;
			margin-top: 2px;
		}
	}
</style>
